/**
 * Fund Compliance — Assignment matrix.
 *
 * For each section:
 *   • A section-level Yes/No lets the admin select the entire section at
 *     once. If No, an N/A reason is captured at section level and cascaded
 *     to every item.
 *   • Per-item Yes/No + assignee/N-A reason overrides the section default.
 *
 * The backend persists both — `compliance_fund_section_assignments` for the
 * section-level decision and `compliance_fund_assignments` for each item.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader, ChevronLeft, Save, CheckCircle } from 'lucide-react';
import { complianceAPI } from '../services/api';

const FundCompliance = () => {
  const { fundId } = useParams();
  const [data, setData] = useState(null);
  const [team, setTeam] = useState([]);
  const [rows, setRows] = useState({});          // item_id -> {applicable,na_reason,assignee_id, overridden}
  const [sectionRows, setSectionRows] = useState({}); // section_id -> {applicable,na_reason}
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(null);

  const fetchAll = async () => {
    const [asg, tm] = await Promise.all([
      complianceAPI.getFundAssignments(fundId),
      complianceAPI.listTeamMembers(),
    ]);
    setData(asg.data);
    setTeam(tm.data || []);
    const map = {};
    (asg.data.items || []).forEach(it => {
      const a = asg.data.assignment_by_item?.[it.id];
      map[it.id] = {
        applicable: a?.applicable ?? null,
        na_reason: a?.na_reason || '',
        assignee_id: a?.assignee_id || it.default_assignee_id || '',
      };
    });
    setRows(map);
    const secMap = {};
    (asg.data.sections || []).forEach(s => {
      const sa = asg.data.section_assignment_by_id?.[s.id];
      secMap[s.id] = {
        applicable: sa?.applicable ?? null,
        na_reason: sa?.na_reason || '',
      };
    });
    setSectionRows(secMap);
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [fundId]);

  const itemsBySection = useMemo(() => {
    const map = {};
    (data?.items || []).forEach(it => {
      (map[it.section_id] = map[it.section_id] || []).push(it);
    });
    return map;
  }, [data]);

  const patchRow = (itemId, patch) =>
    setRows(r => ({ ...r, [itemId]: { ...r[itemId], ...patch, overridden: true } }));

  const patchSection = (sectionId, patch) => {
    setSectionRows(prev => ({ ...prev, [sectionId]: { ...prev[sectionId], ...patch } }));
    // Cascade to items that haven't been individually overridden
    const items = itemsBySection[sectionId] || [];
    setRows(prev => {
      const next = { ...prev };
      items.forEach(it => {
        const existing = next[it.id] || {};
        if (existing.overridden) return; // per-item override wins
        if (patch.applicable === true) {
          next[it.id] = {
            ...existing,
            applicable: true,
            na_reason: '',
            assignee_id: existing.assignee_id || it.default_assignee_id || '',
          };
        } else if (patch.applicable === false) {
          next[it.id] = {
            ...existing,
            applicable: false,
            na_reason: patch.na_reason ?? existing.na_reason ?? '',
            assignee_id: '',
          };
        } else if (patch.applicable === null) {
          // cleared — reset per-item state
          next[it.id] = { ...existing, applicable: null, na_reason: '' };
        }
      });
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSavedMsg(null);
    try {
      const section_rows = Object.entries(sectionRows)
        .filter(([, sr]) => sr.applicable !== null && sr.applicable !== undefined)
        .map(([section_id, sr]) => ({
          section_id,
          applicable: !!sr.applicable,
          na_reason: sr.applicable ? null : (sr.na_reason || null),
        }));

      const item_rows = Object.entries(rows)
        .filter(([, r]) => r.applicable !== null && r.overridden)
        .map(([item_id, r]) => ({
          item_id,
          applicable: !!r.applicable,
          na_reason: r.applicable ? null : (r.na_reason || null),
          assignee_id: r.applicable ? (r.assignee_id || null) : null,
        }));

      await complianceAPI.saveFundAssignments(fundId, item_rows, section_rows);
      setSavedMsg('Assignments saved · occurrences regenerated');
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
      setTimeout(() => setSavedMsg(null), 3000);
    }
  };

  if (!data) return <div className="p-6"><Loader className="animate-spin" /></div>;

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="fund-compliance-page">
      <div className="flex items-center justify-between mb-6 gap-4">
        <div>
          <Link to="/compliance" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mb-1">
            <ChevronLeft size={14} /> Back to Tracker
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Fund Compliance — {data.fund?.name}</h1>
          <p className="text-gray-600 mt-1">
            Pick which compliance items apply to this fund. Answer the whole section at once with the section-level Yes / No, or override any row individually.
            Assignee is optional — unassigned items are highlighted red in the Tracker so you can assign them later.
          </p>
        </div>
        <button onClick={handleSave} disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                data-testid="save-assignments-btn">
          {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
          Save
        </button>
      </div>

      {savedMsg && (
        <div className="mb-3 text-sm text-emerald-700 inline-flex items-center gap-1"><CheckCircle size={14} /> {savedMsg}</div>
      )}

      {(data.sections || []).length === 0 && (
        <div className="bg-white border rounded-lg p-6 text-center text-gray-500">
          No compliance sections defined yet. Ask an admin to set up the Compliance Database first.
          <div className="mt-2">
            <Link to="/compliance/database" className="text-blue-600 hover:underline text-sm">Manage Compliance Database →</Link>
          </div>
        </div>
      )}

      {(data.sections || []).map(sec => {
        const items = itemsBySection[sec.id] || [];
        if (items.length === 0) return null;
        const secState = sectionRows[sec.id] || { applicable: null, na_reason: '' };
        const sectionNo = secState.applicable === false;
        return (
          <div key={sec.id} className="bg-white border rounded-lg mb-4 overflow-hidden" data-testid={`fc-section-${sec.id}`}>
            {/* Section-level header */}
            <div className="px-4 py-3 bg-gray-50 border-b flex flex-wrap items-center gap-3">
              <div className="font-semibold text-sm flex-1 min-w-[200px]">{sec.name}</div>
              <div className="text-xs text-gray-500 mr-1">Apply to entire section:</div>
              <div className="flex gap-3 text-xs">
                <label className="inline-flex items-center gap-1">
                  <input type="radio" name={`sec-app-${sec.id}`} checked={secState.applicable === true}
                         onChange={() => patchSection(sec.id, { applicable: true })}
                         data-testid={`fc-sec-yes-${sec.id}`} />
                  Yes — all applicable
                </label>
                <label className="inline-flex items-center gap-1">
                  <input type="radio" name={`sec-app-${sec.id}`} checked={secState.applicable === false}
                         onChange={() => patchSection(sec.id, { applicable: false })}
                         data-testid={`fc-sec-no-${sec.id}`} />
                  No — none applicable
                </label>
                {secState.applicable !== null && (
                  <button onClick={() => patchSection(sec.id, { applicable: null, na_reason: '' })}
                          className="text-blue-600 hover:underline"
                          data-testid={`fc-sec-clear-${sec.id}`}>
                    Clear
                  </button>
                )}
              </div>
              {sectionNo && (
                <input type="text" value={secState.na_reason || ''}
                       onChange={e => patchSection(sec.id, { na_reason: e.target.value })}
                       placeholder="Section-level N/A reason (required)"
                       className="w-full sm:w-80 border rounded px-2 py-1 text-sm"
                       data-testid={`fc-sec-na-${sec.id}`} />
              )}
            </div>

            <table className="w-full text-sm">
              <thead className="bg-gray-50/40 border-b text-xs">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-700 w-24">Applicable</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-700">Compliance</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-700 w-48">Assignee</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-700">N/A Reason</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => {
                  const row = rows[it.id] || { applicable: null };
                  return (
                    <tr key={it.id} className="border-t" data-testid={`fc-item-row-${it.id}`}>
                      <td className="px-4 py-2">
                        <div className="flex gap-3 text-xs">
                          <label className="inline-flex items-center gap-1">
                            <input type="radio" name={`app-${it.id}`} checked={row.applicable === true}
                                   onChange={() => patchRow(it.id, { applicable: true })}
                                   data-testid={`fc-yes-${it.id}`} />
                            Yes
                          </label>
                          <label className="inline-flex items-center gap-1">
                            <input type="radio" name={`app-${it.id}`} checked={row.applicable === false}
                                   onChange={() => patchRow(it.id, { applicable: false })}
                                   data-testid={`fc-no-${it.id}`} />
                            No
                          </label>
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="font-medium text-gray-900">{it.name}</div>
                        <div className="text-xs text-gray-500 capitalize">
                          {(it.compliance_type || '').replace('_', '-')}
                          {it.compliance_type === 'recurring' && it.frequency ? ` · ${it.frequency}` : ''}
                          {it.compliance_type === 'event_based' && it.event_trigger ? ` · ${it.event_trigger}` : ''}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        {row.applicable === true ? (
                          <select value={row.assignee_id || ''} onChange={e => patchRow(it.id, { assignee_id: e.target.value })}
                                  className="w-full border rounded px-2 py-1 text-sm"
                                  data-testid={`fc-assignee-${it.id}`}>
                            <option value="">— Unassigned (flagged in tracker) —</option>
                            {team.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                          </select>
                        ) : <span className="text-gray-400 text-xs italic">—</span>}
                      </td>
                      <td className="px-4 py-2">
                        {row.applicable === false ? (
                          <input type="text" value={row.na_reason || ''}
                                 onChange={e => patchRow(it.id, { na_reason: e.target.value })}
                                 className="w-full border rounded px-2 py-1 text-sm"
                                 placeholder="Reason (required)"
                                 data-testid={`fc-na-${it.id}`} />
                        ) : <span className="text-gray-400 text-xs italic">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
};

export default FundCompliance;
